import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { Autocomplete, Box, Chip, FilterOptionsState, TextField, Typography } from '@mui/material';

import { PartnerResult } from 'stores/PartnerStoreAdapter';

// --- 共通Props ---

type PartnerSelectBaseProps = {
  /** 全パートナーリスト */
  partners: PartnerResult[];
  /** ラベル */
  label?: string;
  /** エラー表示 */
  error?: boolean;
  /** エラーメッセージ */
  helperText?: string;
  /** 無効状態 */
  disabled?: boolean;
  /** 必須表示 */
  required?: boolean;
};

// --- 複数選択Props ---

type PartnerSelectMultiProps = PartnerSelectBaseProps & {
  /** 選択モード: 複数 */
  mode: 'multi';
  /** 選択中のパートナーIDリスト */
  selectedPartnerIds: string[];
  /** 選択変更ハンドラ（複数） */
  onChange: (partnerIds: string[]) => void;
};

// --- 単一選択Props ---

type PartnerSelectSingleProps = PartnerSelectBaseProps & {
  /** 選択モード: 単一 */
  mode: 'single';
  /** 選択中のパートナーID（null=未選択） */
  selectedPartnerId: string | null;
  /** 選択変更ハンドラ（単一） */
  onChange: (partnerId: string | null) => void;
};

export type PartnerSelectProps = PartnerSelectMultiProps | PartnerSelectSingleProps;

/**
 * パートナー選択コンポーネント
 *
 * Autocomplete ベースでパートナーを検索・選択可能。
 * mode="multi" で複数選択、mode="single" で単一選択。
 * パートナー名・カナ・担当者名で絞り込みができる。
 */
const PartnerSelect: React.FC<PartnerSelectProps> = (props) => {
  const {
    partners,
    label = 'パートナー',
    error = false,
    helperText,
    disabled = false,
    required = false,
  } = props;

  const filterOptions = (options: PartnerResult[], state: FilterOptionsState<PartnerResult>) => {
    const input = state.inputValue.toLowerCase();
    if (!input) return options;
    return options.filter(
      (p) =>
        p.partnerName.toLowerCase().includes(input) ||
        p.partnerNameKana.toLowerCase().includes(input) ||
        (p.contactName && p.contactName.toLowerCase().includes(input)),
    );
  };

  const renderOption = (
    optionProps: React.HTMLAttributes<HTMLLIElement> & { key: React.Key },
    option: PartnerResult,
  ) => {
    const { key, ...rest } = optionProps;
    return (
      <Box
        component="li"
        key={String(key)}
        {...rest}
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}
      >
        <HandshakeIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500} noWrap>
            {option.partnerName}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {option.partnerNameKana}
            {option.contactName && ` / ${option.contactName}`}
          </Typography>
        </Box>
      </Box>
    );
  };

  if (props.mode === 'single') {
    const selectedPartner = partners.find((p) => p.partnerId === props.selectedPartnerId) || null;

    return (
      <Autocomplete
        options={partners}
        value={selectedPartner}
        onChange={(_, newValue) => props.onChange(newValue?.partnerId || null)}
        getOptionLabel={(option) => option.partnerName}
        filterOptions={filterOptions}
        isOptionEqualToValue={(option, value) => option.partnerId === value.partnerId}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            size="small"
            required={required}
            error={error}
            helperText={helperText}
            placeholder="パートナーを検索..."
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        )}
        renderOption={renderOption}
        noOptionsText="該当するパートナーがありません"
        sx={{ width: '100%' }}
      />
    );
  }

  // mode === 'multi'
  const selectedPartners = partners.filter((p) => props.selectedPartnerIds.includes(p.partnerId));

  return (
    <Autocomplete
      multiple
      options={partners}
      value={selectedPartners}
      onChange={(_, newValue) => props.onChange(newValue.map((p) => p.partnerId))}
      getOptionLabel={(option) => option.partnerName}
      filterOptions={filterOptions}
      isOptionEqualToValue={(option, value) => option.partnerId === value.partnerId}
      disabled={disabled}
      disableCloseOnSelect
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          required={required}
          error={error}
          helperText={helperText}
          placeholder={selectedPartners.length === 0 ? 'パートナーを検索...' : ''}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      )}
      renderOption={renderOption}
      renderTags={(value, getTagProps) =>
        value.map((partner, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              {...tagProps}
              label={partner.partnerName}
              size="small"
              icon={<HandshakeIcon sx={{ fontSize: 16 }} />}
              deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
              sx={{
                borderRadius: 1.5,
                fontWeight: 500,
                '& .MuiChip-icon': { color: 'primary.main' },
              }}
            />
          );
        })
      }
      noOptionsText="該当するパートナーがありません"
      sx={{ width: '100%' }}
    />
  );
};

export default PartnerSelect;
